import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ErrorSnackBarData, SnackBarService } from '@qtek/libs/snack-bar-core';
import { of } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';

import { Router } from '@angular/router';
import { AuthUserActions } from '@qtek/libs/auth-user';
import { Message } from '@qtek/shared/models';
import { AppState } from '../index';
import { PermissionsActions } from '../permission';
import { CompanyService } from './company.service';
import { PersonService } from './person.service';
import { UserActions } from './user.actions';
import { UserStoreService } from './user.service';

@Injectable()
export class UserEffects {
  currentUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getCurrentUser),
      switchMap(({ payload }) =>
        this.userService.getCurrentUser(payload).pipe(
          map(res => {
            this.store.dispatch(
              AuthUserActions.getUserSuccess({ user: res.res })
            );

            if (res.res?.lic?.features) {
              this.store.dispatch(
                PermissionsActions.setPermissions({
                  permissions: res.res.lic.features,
                })
              );
            } else {
              const snackBarData: ErrorSnackBarData = {
                type: 'ErrorMessage',
                message: 'GNR_MISSED_PERMISSIONS',
              };
              this.snackBar.showSnackBar(snackBarData);
              this.router.navigate(['error']);
            }
            return UserActions.getCurrentUserSuccess({ payload: res.res });
          }),
          catchError(res => {
            this.store.dispatch(
              AuthUserActions.getUserFailure({ error: new Error('not logged') })
            );
            return of(UserActions.getCurrentUserFailure({ payload: res?.sts }));
          })
        )
      )
    )
  );

  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateCurrentUser),
      concatMap(({ payload }) =>
        this.personService
          .updatePerson(payload.form, payload.additionalPayload)
          .pipe(
            map(({ res }) =>
              UserActions.updateCurrentUserSuccess({
                payload: res,
                message: new Message('GNR_PRS_INFO_SAVED'),
              })
            ),
            catchError(({ sts }) =>
              of(UserActions.updateCurrentUserFailure({ payload: sts }))
            )
          )
      )
    )
  );

  updateCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateCurrentUserCompany),
      switchMap(({ payload }) =>
        this.companyService
          .updateCompany(payload.additionalPayload, payload.form)
          .pipe(
            map(({ res }) =>
              UserActions.updateCurrentUserCompanySuccess({
                payload: res,
                message: new Message('GNR_CMP_INFO_SAVED'),
              })
            ),
            catchError(({ sts }) =>
              of(UserActions.updateCurrentUserCompanyFailure({ payload: sts }))
            )
          )
      )
    )
  );

  reloadAfterPermissionsRefresh$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.updateCurrentUserCompanySuccess),
        filter(action => !action.skipReload),
        tap(() =>
          this.store.dispatch(
            UserActions.getCurrentUser({
              payload: { skipRevalidateErrorHandling: true },
            })
          )
        )
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private userService: UserStoreService,
    private personService: PersonService,
    private companyService: CompanyService,
    private store: Store<AppState>,
    private snackBar: SnackBarService,
    private router: Router
  ) {}
}
